import React, { FC, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { GeometryType, LayerReferenceConfigurationDc } from "@evergis/api";
import { Icon, IconButton } from "@evergis/ui";

import { HandBookControlsContainer } from "./styled";
import { SearchField } from "../../../LayerList/components/LayerListSearch/styled";
import { updateAttributesForNewFeature } from "../../../../ducks/editFeature/editFeature";
import {
  setLayerToShow,
  setPreviousFeatureReference,
  setFeatureSearchString,
} from "../../../../ducks/showFeature/showFeature";
import { getSearchString } from "../../../../ducks/showFeature/selectors";
import { useAddRelatedLayerDialog } from "../../../../hooks/useDialogs";
import { usePostgresLayer } from "../../../../hooks/layer/usePostgresLayer";
import { useProjectLayers } from "../../../../hooks/project/useProjectLayers";
import { useResetFeatureSelect } from "../../../FeatureSelector/hooks/useResetFeatureSelect";
import { useFeatureCreator } from "../../../LayerList/hooks/useFeatureCreator";
import { useAction } from "../../../../hooks/useAction";
import { FeatureAttribute } from "../Attributes/FeatureAttributeField";
import { PreviousFeatureReference } from "../../../../ducks/showFeature/types";
import { FeatureDcExtended } from "../../../../ducks/selectFeatures/types";

export interface HandBookControlsProps {
  layerReference: LayerReferenceConfigurationDc;
  attributes: FeatureAttribute[];
  feature: FeatureDcExtended;
  previousFeatureReference?: PreviousFeatureReference;
}

export const HandBookControls: FC<HandBookControlsProps> = ({
  layerReference,
  attributes,
  feature,
  previousFeatureReference,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const resetSelected = useResetFeatureSelect();
  const searchString = useSelector(getSearchString);
  const setSearchString = useAction(setFeatureSearchString);
  const projectLayers = useProjectLayers();
  const openAddRelatedLayerDialog = useAddRelatedLayerDialog();
  const { postgresLayer: referenceLayer } = usePostgresLayer(layerReference?.layerName);

  const [, handleAddFeature] = useFeatureCreator(
    layerReference?.layerName,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    referenceLayer?.geometryType as GeometryType,
  );

  const updateForNewFeature = useMemo(() => {
    const targetAttribute = attributes.find(({ name }) => name === layerReference.targetAttribute);

    return { [layerReference?.referenceAttribute]: targetAttribute?.value };
  }, [layerReference, attributes]);

  const addObject = useCallback(() => {
    resetSelected();
    dispatch(setLayerToShow(layerReference?.layerName));
    dispatch(updateAttributesForNewFeature(updateForNewFeature));
    dispatch(
      setPreviousFeatureReference({
        id: feature?.id,
        layer: feature?.layer,
        reference: layerReference,
        previous: previousFeatureReference,
      }),
    );
  }, [
    resetSelected,
    dispatch,
    layerReference,
    updateForNewFeature,
    feature?.id,
    feature?.layer,
    previousFeatureReference,
  ]);

  const addFeature = useCallback(() => {
    handleAddFeature();
    dispatch(updateAttributesForNewFeature(updateForNewFeature));
    dispatch(
      setPreviousFeatureReference({
        id: feature?.id,
        layer: feature?.layer,
        reference: layerReference,
        previous: previousFeatureReference,
      }),
    );
  }, [dispatch, handleAddFeature, updateForNewFeature, feature, layerReference, previousFeatureReference]);

  const addObjectOrFeature = useCallback(() => {
    if (referenceLayer?.geometryType === GeometryType.Unknown) {
      addObject();
    } else {
      addFeature();
    }
  }, [addFeature, addObject, referenceLayer?.geometryType]);

  const onClickAdd = useCallback(() => {
    if (referenceLayer && !projectLayers.some(({ name }) => name === layerReference?.layerName)) {
      openAddRelatedLayerDialog([referenceLayer.name, referenceLayer.alias], { addToMap: addObjectOrFeature });

      return;
    }

    addObjectOrFeature();
  }, [addObjectOrFeature, layerReference?.layerName, openAddRelatedLayerDialog, projectLayers, referenceLayer]);

  return (
    <HandBookControlsContainer>
      <Icon kind="search" />
      <SearchField
        value={searchString}
        placeholder={`${t("search", { ns: "common" })}...`}
        width={"100%"}
        withSearchIcon={false}
        onEraseClick={() => setSearchString("")}
        onChange={e => setSearchString(e.target.value)}
      />
      <IconButton onClick={onClickAdd} kind={"feature_add"} />
    </HandBookControlsContainer>
  );
};
